import { currentUserId } from "./current-user";

type JqlOp = "=" | "!=" | "~" | "in" | "not_in" | ">" | "<" | ">=" | "<=" | "is_empty" | "is_not_empty";

interface Atom {
  field: string;
  op: JqlOp;
  value: string | string[];
}

export interface ClauseGroup {
  joiner: "AND" | "OR";
  atoms: Atom[];
}

// The fields evaluateJql actually reads — satisfied by both the full Issue and the light
// IssueCard, so the local-first store can filter cards client-side without inflating them to
// full issues. (Kept structural rather than importing Issue/IssueCard so jql.ts has no cycle
// back into the heavy types.)
export interface JqlEvaluable {
  summary: string;
  type: string;
  priority: string;
  statusId: string;
  assigneeUserId: string | null;
  reporterUserId: string | null;
  labels: string[];
  key: string;
  projectKey: string;
  createdAt: string;
  updatedAt: string;
  dueDate: string | null;
  startDate: string | null;
}

const FIELDS: ReadonlySet<string> = new Set<string>([
  "summary",
  "type",
  "priority",
  "status",
  "assignee",
  "reporter",
  "labels",
  "key",
  "project",
  "created",
  "updated",
  "due",
  "start"
]);

const DATE_FIELDS: ReadonlySet<string> = new Set<string>(["created", "updated", "due", "start"]);

// Field/date name lists shared with the autocomplete suggester (jql-suggest.ts) so the
// two stay in lockstep — a field added here is offered as a suggestion automatically.
export const JQL_FIELD_NAMES: readonly string[] = [...FIELDS];
export const JQL_DATE_FIELD_NAMES: readonly string[] = [...DATE_FIELDS];
export const JQL_ARRAY_FIELD_NAMES: readonly string[] = ["labels"];
export const JQL_TEXT_FIELD_NAMES: readonly string[] = ["summary", "key"];

const STOP_WORDS: ReadonlySet<string> = new Set<string>(["and", "AND", "or", "OR"]);

function tokenize(input: string): string[] {
  const tokens: string[] = [];
  let i: number = 0;
  while (i < input.length) {
    const ch: string = input[i]!;
    if (ch === " " || ch === "\t") {
      i += 1;
      continue;
    }
    if (ch === '"') {
      let end: number = input.indexOf('"', i + 1);
      if (end === -1) end = input.length;
      tokens.push(input.slice(i + 1, end));
      i = end + 1;
      continue;
    }
    if (ch === "<" || ch === ">") {
      if (input[i + 1] === "=") {
        tokens.push(ch + "=");
        i += 2;
        continue;
      }
      tokens.push(ch);
      i += 1;
      continue;
    }
    if (ch === "(" || ch === ")" || ch === "," || ch === "=" || ch === "!" || ch === "~") {
      if (ch === "!" && input[i + 1] === "=") {
        tokens.push("!=");
        i += 2;
        continue;
      }
      tokens.push(ch);
      i += 1;
      continue;
    }
    let j: number = i;
    while (j < input.length && !' \t()=!~,"<>'.includes(input[j]!)) j += 1;
    tokens.push(input.slice(i, j));
    i = j;
  }
  return tokens;
}

export function parseJql(input: string): ClauseGroup | null {
  if (!input.trim()) return null;
  const tokens: string[] = tokenize(input);
  const atoms: Atom[] = [];
  let joiner: "AND" | "OR" = "AND";
  let i: number = 0;
  while (i < tokens.length) {
    const field: string = tokens[i]!;
    if (STOP_WORDS.has(field)) {
      joiner = field.toUpperCase() === "OR" ? "OR" : joiner;
      i += 1;
      continue;
    }
    if (!FIELDS.has(field.toLowerCase())) return null;
    const op: string = tokens[i + 1] ?? "";
    // "is empty" / "is not empty" — null/emptiness checks, no value token.
    if (op.toLowerCase() === "is") {
      const isNot: boolean = (tokens[i + 2] ?? "").toLowerCase() === "not";
      const emptyToken: string = (tokens[i + (isNot ? 3 : 2)] ?? "").toLowerCase();
      if (emptyToken !== "empty") return null;
      atoms.push({ field: field.toLowerCase(), op: isNot ? "is_not_empty" : "is_empty", value: "" });
      i += isNot ? 4 : 3;
      continue;
    }
    // "not in (…)" — negated membership.
    const isNotIn: boolean = op.toLowerCase() === "not" && (tokens[i + 2] ?? "").toLowerCase() === "in";
    const COMPARE_OPS: ReadonlySet<string> = new Set<string>(["=", "!=", "~", ">", "<", ">=", "<="]);
    if (!COMPARE_OPS.has(op) && op.toLowerCase() !== "in" && !isNotIn) return null;
    if (op.toLowerCase() === "in" || isNotIn) {
      // expecting (a, b, c) — for "not in" the list starts one token later
      const listStart: number = isNotIn ? i + 3 : i + 2;
      if (tokens[listStart] !== "(") return null;
      const values: string[] = [];
      let j: number = listStart + 1;
      while (j < tokens.length && tokens[j] !== ")") {
        if (tokens[j] === ",") {
          j += 1;
          continue;
        }
        values.push(tokens[j]!);
        j += 1;
      }
      atoms.push({ field: field.toLowerCase(), op: isNotIn ? "not_in" : "in", value: values });
      i = j + 1;
    } else {
      let value: string = tokens[i + 2] ?? "";
      let advance: number = 3;
      // Function-call values: now() / currentUser()
      const lowered: string = value.toLowerCase();
      if ((lowered === "now" || lowered === "currentuser") && tokens[i + 3] === "(" && tokens[i + 4] === ")") {
        value = `${lowered}()`;
        advance = 5;
      }
      atoms.push({ field: field.toLowerCase(), op: op as JqlOp, value });
      i += advance;
    }
  }
  return { joiner, atoms };
}

// Allowed values for the closed-domain fields, lowercased. Fields absent here (assignee,
// reporter, labels, summary, key, dates) have open/large domains and accept any value.
export interface JqlValueDomains {
  priority?: ReadonlySet<string>;
  type?: ReadonlySet<string>;
  status?: ReadonlySet<string>;
  project?: ReadonlySet<string>;
}

const EXACT_VALUE_OPS: ReadonlySet<string> = new Set<string>(["=", "!=", "in", "not_in"]);
const VALUE_FUNCTIONS: ReadonlySet<string> = new Set<string>(["currentuser()", "now()"]);

// Semantic check on a parsed clause: a value compared with an EXACT operator (= != in not in)
// against a closed-domain field must be a known value, so `priority = me` is rejected while
// `priority = medium` passes. Substring (~), comparison (>/<…), and emptiness ops accept any
// value, as do open-domain fields. Returns the first offending `field "value"` for the inline
// error, or null when the clause is fully valid. Mirrors what the suggester offers, so "only
// commit valid JQL" lines up with autocomplete.
export function validateJqlSemantics(group: ClauseGroup, domains: JqlValueDomains): string | null {
  for (const atom of group.atoms) {
    if (!EXACT_VALUE_OPS.has(atom.op)) continue;
    let domain: ReadonlySet<string> | undefined;
    if (atom.field === "priority") domain = domains.priority;
    else if (atom.field === "type") domain = domains.type;
    else if (atom.field === "status") domain = domains.status;
    else if (atom.field === "project") domain = domains.project;
    if (!domain) continue;
    const values: string[] = Array.isArray(atom.value) ? atom.value : [atom.value];
    for (const raw of values) {
      const value: string = raw.toLowerCase();
      if (VALUE_FUNCTIONS.has(value)) continue;
      if (!domain.has(value)) return `Unknown ${atom.field}: "${raw}"`;
    }
  }
  return null;
}

function issueField(issue: JqlEvaluable, field: string): string | string[] {
  switch (field) {
    case "summary":
      return issue.summary.toLowerCase();
    case "type":
      return issue.type;
    case "priority":
      return issue.priority;
    case "status":
      return issue.statusId;
    case "assignee":
      return (issue.assigneeUserId ?? "").toLowerCase();
    case "reporter":
      return (issue.reporterUserId ?? "").toLowerCase();
    case "labels":
      return issue.labels.map((entry: string) => entry.toLowerCase());
    case "key":
      return issue.key.toLowerCase();
    case "project":
      return issue.projectKey.toLowerCase();
    default:
      return "";
  }
}

function issueDate(issue: JqlEvaluable, field: string): string | null {
  switch (field) {
    case "created":
      return issue.createdAt;
    case "updated":
      return issue.updatedAt;
    case "due":
      return issue.dueDate;
    case "start":
      return issue.startDate;
    default:
      return null;
  }
}

// Resolves a JQL date value to epoch ms. Supports now(), relative day offsets
// like -7d / 14d, and absolute ISO dates (e.g. 2026-05-28). Returns null if
// the value is not a recognizable date.
function resolveDateValue(raw: string): number | null {
  const value: string = raw.trim().toLowerCase();
  if (value === "now()") return Date.now();
  const relative: RegExpMatchArray | null = value.match(/^(-?\d+)d$/);
  if (relative) {
    const days: number = Number(relative[1]);
    return Date.now() + days * 24 * 60 * 60 * 1000;
  }
  const parsed: number = Date.parse(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

function compareDates(actualMs: number, expectedMs: number, op: JqlOp): boolean {
  if (op === ">") return actualMs > expectedMs;
  if (op === "<") return actualMs < expectedMs;
  if (op === ">=") return actualMs >= expectedMs;
  if (op === "<=") return actualMs <= expectedMs;
  // For = / != on dates, compare by calendar day rather than exact instant.
  const sameDay: boolean =
    new Date(actualMs).toISOString().slice(0, 10) === new Date(expectedMs).toISOString().slice(0, 10);
  if (op === "=") return sameDay;
  if (op === "!=") return !sameDay;
  return false;
}

export function evaluateJql(group: ClauseGroup, issue: JqlEvaluable): boolean {
  const checks: boolean[] = group.atoms.map((atom: Atom) => {
    // Emptiness checks work across scalar, array, and date fields.
    if (atom.op === "is_empty" || atom.op === "is_not_empty") {
      let isEmpty: boolean;
      if (DATE_FIELDS.has(atom.field)) {
        isEmpty = !issueDate(issue, atom.field);
      } else {
        const fieldValue: string | string[] = issueField(issue, atom.field);
        isEmpty = Array.isArray(fieldValue) ? fieldValue.length === 0 : fieldValue === "";
      }
      return atom.op === "is_empty" ? isEmpty : !isEmpty;
    }
    // Date fields use temporal comparison rather than string matching.
    if (DATE_FIELDS.has(atom.field) && !Array.isArray(atom.value)) {
      const rawActual: string | null = issueDate(issue, atom.field);
      if (!rawActual) return false;
      const actualMs: number = Date.parse(rawActual);
      const expectedMs: number | null = resolveDateValue(atom.value);
      if (Number.isNaN(actualMs) || expectedMs === null) return false;
      return compareDates(actualMs, expectedMs, atom.op);
    }
    const actual: string | string[] = issueField(issue, atom.field);
    // currentUser() resolves to the signed-in user's id for assignee/reporter.
    let resolvedValue: string | string[] = atom.value;
    if (!Array.isArray(atom.value) && atom.value.toLowerCase() === "currentuser()") {
      resolvedValue = currentUserId();
    }
    const expected: string | string[] = Array.isArray(resolvedValue)
      ? resolvedValue.map((entry: string) => entry.toLowerCase())
      : resolvedValue.toLowerCase();
    if ((atom.op === "in" || atom.op === "not_in") && Array.isArray(expected)) {
      const member: boolean = Array.isArray(actual)
        ? actual.some((value: string) => expected.includes(value))
        : expected.includes(actual);
      return atom.op === "in" ? member : !member;
    }
    if (atom.op === "~") {
      const haystack: string = Array.isArray(actual) ? actual.join(" ") : actual;
      return haystack.includes(String(expected));
    }
    if (atom.op === "=") {
      if (Array.isArray(actual)) return actual.includes(String(expected));
      return actual === expected;
    }
    if (atom.op === "!=") {
      if (Array.isArray(actual)) return !actual.includes(String(expected));
      return actual !== expected;
    }
    return false;
  });
  if (checks.length === 0) return true;
  if (group.joiner === "OR") return checks.some((value: boolean) => value);
  return checks.every((value: boolean) => value);
}
